import I18n from 'i18n-js';
import { citationsLandingPluralCategory } from 'js/citations_landing/citationsLandingPluralCategory';
import { isRtlLocale } from 'js/utils/rtl';

/**
 * I18n.t, isolating interpolated values.
 *
 * Prices and counts arrive Latin-script ("$99"). Dropped raw into an RTL
 * sentence the bidi algorithm reorders them against neighbouring punctuation,
 * so "$99" renders as "99$". Wrapping each in FSI/PDI resolves its direction
 * without leaking into the sentence.
 */

const FSI = '⁨';
const PDI = '⁩';

// `count` drives pluralization and must stay a bare number; its display twin
// `count_delimited` carries the formatted text and does get isolated.
const NEVER_ISOLATE = new Set(['count']);

// Scalars only: I18n.t stringifies whatever it's given, so a React element here
// would render as "[object Object]". undefined is fine for a key without that slot.
export type TranslateValues = Record<string, string | number | undefined>;

function isolate(values: TranslateValues): TranslateValues {
  const out: TranslateValues = {};
  for (const [key, value] of Object.entries(values)) {
    const isolatable =
      !NEVER_ISOLATE.has(key) &&
      (typeof value === 'string' || typeof value === 'number');
    out[key] = isolatable ? `${FSI}${value}${PDI}` : value;
  }
  return out;
}

export function isolatedTranslate(
  key: string,
  values?: TranslateValues,
): string {
  if (!values || !isRtlLocale(I18n.currentLocale())) return I18n.t(key, values);

  return I18n.t(key, isolate(values));
}

/**
 * isolatedTranslate for a counted key ({one:, few:, other:, ...} in en.yml). i18n-js's own
 * pluralizer is locale-blind (count === 1 -> 'one', else 'other'), so ru/pl/uk/ar would read the
 * wrong form for 2-4; this picks the CLDR category itself. `count` is the raw number -- pass its
 * display form (e.g. formatStat(count)) as its own placeholder.
 */
export function isolatedTranslatePlural(
  key: string,
  count: number,
  values?: TranslateValues,
): string {
  const category = citationsLandingPluralCategory(I18n.currentLocale(), count);

  return isolatedTranslate(`${key}.${category}`, { count, ...values });
}

function has(lang: string, key: string): boolean {
  let node: unknown = I18n.translations[lang];
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) return false;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string';
}

export interface Translate {
  t: (key: string, values?: TranslateValues) => string;
  tPlural: (key: string, count: number, values?: TranslateValues) => string;
}

export interface PageTranslation extends Translate {
  // t/tPlural in a fixed lang, whatever the page's.
  forLang: (lang: string) => Translate;
  applyLang: (lang: string) => Promise<void>;
  renderIn: <T>(lang: string, render: () => T) => T;
  suspendFor: (lang: string) => void;
  markLoaded: (langs: string[]) => void;
}

/**
 * Translation calls for one page, in a lang only this page's copy follows: the global I18n.locale,
 * which every other bundle on the page reads, is left alone. A key the lang lacks (a declined
 * plural category, a lang whose run didn't finish) falls back to its other form, then English.
 */
export function pageTranslation(
  loaders: Partial<Record<string, () => Promise<unknown>>>,
): PageTranslation {
  let current = 'en';
  // Only the latest applyLang commits, so a slow chunk can't overwrite a later choice.
  let latest = 0;
  const loaded = new Set<string>();
  // A chunk that failed to load renders English rather than suspending on it again forever.
  const failed = new Set<string>();
  const loading = new Map<string, Promise<unknown>>();

  // A lang the page doesn't ship reads as English.
  const shipped = (lang: string): string => (loaders[lang] ? lang : 'en');

  const load = (lang: string): Promise<unknown> => {
    let pending = loading.get(lang);
    if (!pending) {
      pending = (loaders[lang]?.() ?? Promise.resolve()).then(
        () => {
          loaded.add(lang);
        },
        () => {
          failed.add(lang);
          loading.delete(lang);
        },
      );
      loading.set(lang, pending);
    }
    return pending;
  };

  const translateIn = (
    lang: string,
    key: string,
    values?: TranslateValues,
  ): string => {
    const shown = values && isRtlLocale(lang) ? isolate(values) : values;
    return I18n.t(key, { ...shown, locale: lang });
  };

  const forLang = (target: string): Translate => ({
    t: (key, values) =>
      translateIn(has(target, key) ? target : 'en', key, values),
    tPlural: (key, count, values) => {
      // The lang first, then its category: English's own forms if the lang lacks the key.
      const lang = has(target, `${key}.other`) ? target : 'en';
      const category = citationsLandingPluralCategory(lang, count);
      const form = has(lang, `${key}.${category}`) ? category : 'other';
      return translateIn(lang, `${key}.${form}`, { count, ...values });
    },
  });

  return {
    t: (key, values) => forLang(current).t(key, values),
    tPlural: (key, count, values) =>
      forLang(current).tPlural(key, count, values),
    forLang,
    // Loads one lang's bundle and switches this page to it; 'en' or a lang the page doesn't ship
    // switches it back to English.
    applyLang: async (lang) => {
      const call = ++latest;
      const target = shipped(lang);
      if (target !== 'en') await load(target);
      if (call === latest) current = loaded.has(target) ? target : 'en';
    },
    // Server rendering: the lang holds for exactly this synchronous render, so one request's lang
    // can't reach another's. Its bundle must already be loaded (see markLoaded).
    renderIn: (lang, render) => {
      const previous = current;
      const target = shipped(lang);
      current = loaded.has(target) ? target : 'en';
      try {
        return render();
      } finally {
        current = previous;
      }
    },
    // Client rendering, inside <Suspense>: suspends until lang's bundle has loaded, then makes it
    // the page's lang. While it waits, hydration keeps the server's already-translated HTML.
    suspendFor: (lang) => {
      const target = shipped(lang);
      if (target !== 'en' && !loaded.has(target) && !failed.has(target)) {
        throw load(target);
      }
      current = loaded.has(target) ? target : 'en';
    },
    // For bundles imported statically (the server bundle imports every lang).
    markLoaded: (langs) => langs.forEach((lang) => loaded.add(lang)),
  };
}
